import type { Badges } from './social';
import { z } from 'zod';
import { zIdempotencyKey } from './common';
import { zPublicId, type PresenceStatus } from './social';
import { CHAT_TEXT_MAX } from './chat/filter';

/*
 * Chat between friends (docs/superpowers/specs/2026-09-30-social-notify-chat-design.md).
 * Only friends (`Friendship.status = accepted`) may write; text only, no links or contacts (CHAT_REJECTED),
 * bad words are masked (`checkChatText`, ./chat/filter). No token transfers in chat (owner decision).
 */

export { CHAT_TEXT_MIN, CHAT_TEXT_MAX, CHAT_BAD_WORDS, checkChatText, type ChatTextCheck } from './chat/filter';
/** hard cap on the raw input (before trimming): longer bodies are VALIDATION_FAILED, not CHAT_REJECTED */
export const CHAT_TEXT_RAW_MAX = CHAT_TEXT_MAX * 4;
/** history page size (GET /api/chat/:publicId?before=&limit=) */
export const CHAT_PAGE_SIZE = 30;
export const CHAT_PAGE_MAX = 100;
/** messages older than this are deleted by the daily job */
export const CHAT_RETENTION_DAYS = 180;
/** per-sender send limits; accounts younger than CHAT_NEW_ACCOUNT_HOURS get CHAT_LIMIT_PER_MINUTE_NEW */
export const CHAT_LIMIT_PER_SECOND = 1;
export const CHAT_LIMIT_PER_MINUTE = 20;
export const CHAT_LIMIT_PER_DAY = 300;
export const CHAT_LIMIT_PER_MINUTE_NEW = 5;
export const CHAT_NEW_ACCOUNT_HOURS = 24;
/** report: messages of context around the reported one shown to the moderator */
export const CHAT_REPORT_CONTEXT = 10;
export const CHAT_REPORT_NOTE_MAX = 500;
/** toast preview: first N characters of the message */
export const CHAT_TOAST_PREVIEW = 60;

/** `CHAT_REJECTED { reason }` */
export const CHAT_REJECT_REASONS = ['link', 'empty', 'long', 'not_friends', 'blocked'] as const;
export type ChatRejectReason = (typeof CHAT_REJECT_REASONS)[number];

export const CHAT_REPORT_REASONS = ['spam', 'abuse', 'scam', 'other'] as const;
export type ChatReportReason = (typeof CHAT_REPORT_REASONS)[number];

export const CHAT_REPORT_STATUSES = ['open', 'resolved', 'dismissed'] as const;
export type ChatReportStatus = (typeof CHAT_REPORT_STATUSES)[number];

export const zChatMessageId = z.string().uuid();

/** POST /api/chat/:publicId — `text` is checked by `checkChatText` on the server; `clientId` makes a resend idempotent */
export const zChatSend = z
  .object({
    text: z.string().max(CHAT_TEXT_RAW_MAX),
    clientId: zIdempotencyKey,
  })
  .strict();
export type ChatSend = z.infer<typeof zChatSend>;

/** POST /api/chat/:publicId/read — every incoming message up to and including `upTo` becomes read */
export const zChatRead = z.object({ upTo: zChatMessageId }).strict();
export type ChatRead = z.infer<typeof zChatRead>;

/** POST /api/chat/report */
export const zChatReport = z
  .object({
    messageId: zChatMessageId,
    reason: z.enum(CHAT_REPORT_REASONS),
    note: z.string().trim().max(CHAT_REPORT_NOTE_MAX).optional(),
  })
  .strict();
export type ChatReportInput = z.infer<typeof zChatReport>;

/** POST /api/friends/block, POST /api/friends/unblock */
export const zFriendBlock = z.object({ publicId: zPublicId }).strict();
export type FriendBlock = z.infer<typeof zFriendBlock>;

/** GET /api/chat/:publicId?before=<id>&limit= */
export const zChatHistoryQuery = z.object({
  before: zChatMessageId.optional(),
  limit: z.coerce.number().int().min(1).max(CHAT_PAGE_MAX).default(CHAT_PAGE_SIZE),
});
export type ChatHistoryQuery = z.infer<typeof zChatHistoryQuery>;

export interface ChatMessageView {
  id: string;
  /** sender / recipient public numbers */
  from: number;
  to: number;
  /** null when hidden by a moderator ("Message hidden by a moderator") */
  text: string | null;
  hidden: boolean;
  createdAt: string;
  readAt: string | null;
  /** the sender's clientId — only in the sender's own copy (tab sync, optimistic send) */
  clientId: string | null;
}

/** GET /api/chat → { conversations } — one row per partner, newest first */
export interface ChatConversation {
  publicId: number;
  nickname: string;
  avatar: string;
  level: number;
  status: PresenceStatus;
  lastMessage: ChatMessageView | null;
  /** incoming messages not read yet */
  unread: number;
  /** false: not friends any more or blocked — history is read-only */
  canWrite: boolean;
}

/** GET /api/chat/:publicId — messages oldest first; `hasMore` = older page exists (pass `before` = first id) */
export interface ChatHistory {
  partner: Omit<ChatConversation, 'lastMessage' | 'unread'>;
  messages: ChatMessageView[];
  hasMore: boolean;
}

/** socket `chat:read`: `by` (public number) has read every message up to `upTo` that was sent to them */
export interface ChatReadEvent {
  by: number;
  upTo: string;
  at: string;
}

/** server → client, delivered to `userRoom` */
export interface ChatServerToClient {
  /** a new message — to both sides (the sender for tab sync) */
  'chat:message': (m: ChatMessageView) => void;
  /** read receipt — to the partner */
  'chat:read': (e: ChatReadEvent) => void;
  /** counters changed (friend requests, messages, block, unfriend); also sent on connect */
  'badges': (b: Badges) => void;
}
